/**
 * Emotion state is an inner object that defines emotion and can be changed with emotion event.
 *
 * Currect format is an array, first element is 'positive' second element is a quantative number.
 * @typedef {Object} EmotionState
 * @property {number} positive
 * @property {number} amusing
 * @property {number} step
 */

/**
 * Emotion event is an object can change emotion state.
 * Emotion events are emitted by user or system actions.
 *
 * Currect format is a string of 'positive'/'negative'/'amused' or an array ['relative', <0..1>] or ['positive'/'negative'/'amused']
 * @typedef {string|[string]} EmotionEvent
 */

/**
 * @param {string} emotionEventType
 * @param {EmotionState} emotionState - current emotion state
 * @return {EmotionState} changed emotion state
 * @private
 */
 function applySimpleEmotionEvent(emotionEventType, { positive, step }) {
	switch (emotionEventType) {
		case 'positive': return {
			positive: 1,
			amusing: 0,
			step: step + 1
		};
		case 'shoot': return {
			shoot: 1
		};
		case 'negative': return {
			positive: -1,
			amusing: 0,
			step: step + 1
		};
		case 'amused': return {
			positive: 0,
			amusing: 1,
			step: step + 1
		};
		case 'confuse': return {
			positive: -1,
			amusing: 1,
			step: step + 1
		};
		case 'success': return {
			positive: 2,
			amusing: 0,
			step: step + 1
		};
		case 'main': return {
			positive: 0,
			amusing: 0,
			step: step + 1,
			init: 1
		}
		case 'surprise': return {
			surprise: 0
		}
	}
	throw new Error(`Unknown event type ${emotionEventType}`);
}

/**
 * Apply emotion event to currect state.
 * @param {EmotionEvent} emotionEvent - emotion event
 * @param {EmotionState} emotionState - emotion state
 * @return {EmotionState} changed emotion state
 */
export function applyEmotionEvent(emotionEvent, emotionState) {
	if (typeof emotionEvent === 'string') return applySimpleEmotionEvent(emotionEvent, emotionState);
	const [ emotionEventType, quant ] = emotionEvent;
	const { step } = emotionState;
	switch (emotionEventType) {
		case 'positive':
		case 'negative':
		case 'amused':
		case 'confuse':
		case 'success':
		case 'shoot':
		case 'surprise':	
		case 'main': {
			return applySimpleEmotionEvent(emotionEventType, emotionState);		
		}
		case 'relative':
			return {
				positive: step < 5 ? quant : quant - 0.5,
				amusing: 1,
				step: step + 1
			};
	}
}

/**
 * Retrieve emotion from emotion step.
 * @param {EmotionState} emotionState - emotion state
 * @return {string} emotion
 */
export function getEmotion(emotionState) {
	if(emotionState.surprise === 0) {return "surprise";};
	if(emotionState.shoot) { return "shoot"};
	if (emotionState.positive === 1) return "positive";
	if (emotionState.positive === 2) return "success";
	if (emotionState.positive === -1) {
		if (emotionState.amusing === 1) return "confuse" 
		else return 'negative'
	}
	if (emotionState.amusing === 1) return 'amused'
	if (emotionState.positive === 0 && emotionState.amusing === 0 && emotionState.init) return "main";
	return "greetings";
	// if (amusing === 1 && positive >= 1) return 'happy';
	// if (amusing === 1) return 'amused';
	// return positive >= 0 ? 'positive' : 'negative';
}

/**
 * Reset emotion state to use it at the start of minigame.
 * @param {EmotionState} state - previous state
 * @return {EmotionState} state with stats reset
 */
export function resetEmotionState(state) {
	return { positive: 0, amusing: 0, ...state, step: 0 };
}